import React from "react";
import { Link } from "react-router-dom";

const sectionStyle = { marginBottom: 32 };
const h2Style = { color: "#f1f5f9", fontSize: "1.3rem", fontWeight: 700, marginBottom: 8 };
const strongStyle = { color: "#f1f5f9" };

const Privacy = () => {
  return (
    <div style={{
      minHeight: "100vh",
      background: "#0f172a",
      color: "#e2e8f0",
      fontFamily: "Inter, system-ui, sans-serif",
    }}>
      <nav style={{
        padding: "16px 24px",
        borderBottom: "1px solid #1e293b",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <span style={{ fontSize: "1.4rem", fontWeight: 900 }}>
            <span style={{ color: "#f1f5f9" }}>Pay</span>
            <span style={{ color: "#3b82f6" }}>Map</span>
          </span>
        </Link>
        <Link to="/" style={{ color: "#3b82f6", textDecoration: "none", fontWeight: 600 }}>
          ← Back
        </Link>
      </nav>

      <div style={{
        maxWidth: 720,
        margin: "0 auto",
        padding: "48px 24px",
        lineHeight: 1.7,
      }}>
        <h1 style={{ color: "#f1f5f9", fontSize: "2rem", fontWeight: 800, marginBottom: 8 }}>Privacy Policy</h1>
        <p style={{ color: "#94a3b8", marginBottom: 32 }}>Last updated: March 12, 2026</p>

        <section style={sectionStyle}>
          <h2 style={h2Style}>1. Introduction</h2>
          <p>
            PayMap ("we", "our", or "us") is operated by PayMapLabs. This Privacy Policy explains how we access, collect,
            use, and protect your information when you use the PayMap mobile application and website (collectively, the "Service").
            It applies to all users of the Service.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>2. Information We Collect</h2>
          <p><strong style={strongStyle}>Account Information:</strong> When you create an account, we collect your email address. Your password is handled securely by Firebase Authentication and is never stored by us in plain text.</p>
          <p><strong style={strongStyle}>Financial Calendar Data:</strong> Bill names, amounts, due dates, frequencies, categories, income entries, payment tracking, and display preferences you enter into the app. This data is stored in your personal account and is not shared with any third party.</p>
          <p><strong style={strongStyle}>Device Credentials (Biometric Login):</strong> If you enable biometric login, an encrypted copy of your login credentials is stored locally on your device behind your fingerprint or face authentication. We never receive, transmit, or store your biometric data.</p>
          <p><strong style={strongStyle}>App Preferences:</strong> Theme/color selections, notification settings, display preferences, and calendar configuration choices.</p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>3. Information We Do NOT Collect</h2>
          <p>PayMap does not access, collect, or transmit any of the following:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li>Location or GPS data</li>
            <li>Contacts or phonebook data</li>
            <li>Camera or microphone data</li>
            <li>SMS, call logs, or phone state</li>
            <li>Photos, files, or media on your device</li>
            <li>Advertising identifiers or tracking IDs</li>
            <li>Health, fitness, or biometric data (biometric verification is handled entirely by your device's operating system)</li>
            <li>Data from other apps installed on your device</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>4. How We Use Your Information</h2>
          <ul style={{ paddingLeft: 20 }}>
            <li>To provide, maintain, and improve the Service</li>
            <li>To authenticate your identity and secure your account</li>
            <li>To sync your calendar data across your devices via cloud storage</li>
            <li>To schedule local bill reminder notifications on your device (only if you enable them)</li>
          </ul>
          <p>We do not use your data for advertising, profiling, analytics, or any purpose other than providing the Service.</p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>5. Data Storage & Security</h2>
          <p>
            Your data is stored securely using Google Firebase (Cloud Firestore). All data is transmitted over encrypted connections (HTTPS/TLS).
            Access to your data is restricted by Firebase Security Rules so that only you can read and write your own data.
          </p>
          <p>
            We do not sell, rent, trade, or otherwise share your personal data with any third parties for monetary or other consideration.
          </p>
          <p>
            Local data (guest mode calendars, offline backups) is stored in your device's local storage and is accessible only within the app.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>6. Data Sharing</h2>
          <p>We share your data only with the following service provider, and only as necessary to operate the Service:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li><strong style={strongStyle}>Google Firebase</strong> (Google LLC) — Provides authentication and cloud data storage. Google processes your data as a data processor on our behalf. See <a href="https://firebase.google.com/support/privacy" style={{ color: "#3b82f6" }} target="_blank" rel="noopener noreferrer">Firebase Privacy Policy</a>.</li>
          </ul>
          <p>We do not share, sell, or transfer your data to any other third parties. We do not use any advertising SDKs, analytics services, or tracking tools.</p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>7. Third-Party Services</h2>
          <p>The Service relies on the following third-party services:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li><strong style={strongStyle}>Google Firebase Authentication</strong> — Account creation and login (<a href="https://firebase.google.com/support/privacy" style={{ color: "#3b82f6" }} target="_blank" rel="noopener noreferrer">Privacy Policy</a>)</li>
            <li><strong style={strongStyle}>Google Cloud Firestore</strong> — Cloud data storage (<a href="https://cloud.google.com/terms/cloud-privacy-notice" style={{ color: "#3b82f6" }} target="_blank" rel="noopener noreferrer">Privacy Notice</a>)</li>
            <li><strong style={strongStyle}>Google Play Services</strong> — App distribution on Android (<a href="https://policies.google.com/privacy" style={{ color: "#3b82f6" }} target="_blank" rel="noopener noreferrer">Privacy Policy</a>)</li>
          </ul>
          <p>These services have their own privacy policies governing how they handle your information. We encourage you to review them.</p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>8. Biometric Data</h2>
          <p>
            If you enable biometric login, your fingerprint or face data is processed entirely on your device by the operating system (Android BiometricPrompt or iOS Face ID/Touch ID).
            PayMap never receives, transmits, or stores biometric data. Only encrypted login credentials are stored locally on the device, protected by your device's biometric hardware.
            You can disable biometric login at any time from the app's Settings.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>9. Notifications</h2>
          <p>
            Bill reminder notifications are scheduled and delivered locally on your device using the operating system's notification system. No notification data is sent to our servers or any third-party push notification service.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>10. Data Retention</h2>
          <p>
            We retain your account data and calendar data for as long as your account is active.
            Guest mode data is stored only in your device's local storage and persists until you clear app data or uninstall the app.
            Offline backup copies are stored locally and are overwritten on each sync.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>11. Data Deletion & Account Deletion</h2>
          <p>
            You can delete your account and all associated data at any time:
          </p>
          <ul style={{ paddingLeft: 20 }}>
            <li><strong style={strongStyle}>In the app:</strong> Go to Settings → Delete Account. This permanently removes your account, all calendar data from our servers, and clears local data.</li>
            <li><strong style={strongStyle}>On the web:</strong> Visit our <Link to="/delete-account" style={{ color: "#3b82f6" }}>Account Deletion page</Link> and sign in to delete your account.</li>
            <li><strong style={strongStyle}>By email:</strong> Contact us at support@paymaplabs.com and we will process your deletion request within 30 days.</li>
          </ul>
          <p>
            Upon account deletion, all your data (authentication credentials, calendar data, and cloud backups) is permanently and irreversibly removed from our servers. Local data on your device (biometric credentials, offline backups) is also cleared when deleted in-app.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>12. Your Rights</h2>
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li><strong style={strongStyle}>Access</strong> the personal data we hold about you</li>
            <li><strong style={strongStyle}>Correct</strong> inaccurate data (you can edit all your data directly in the app)</li>
            <li><strong style={strongStyle}>Delete</strong> your account and all associated data (see Section 11)</li>
            <li><strong style={strongStyle}>Export</strong> your data (the app provides a JSON export feature)</li>
            <li><strong style={strongStyle}>Withdraw consent</strong> at any time by deleting your account</li>
          </ul>
          <p>To exercise any of these rights, use the in-app features or contact us at the email below.</p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>13. Children's Privacy</h2>
          <p>
            The Service is not directed to anyone under the age of 13. We do not knowingly collect personal information from children under 13.
            If we become aware that we have collected personal data from a child under 13, we will take steps to delete that information promptly.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>14. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of changes by updating the "Last updated" date at the top of this page.
            Continued use of the Service after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>15. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p style={{ color: "#3b82f6", fontWeight: 600 }}>support@paymaplabs.com</p>
          <p style={{ marginTop: 8, color: "#94a3b8", fontSize: "0.9rem" }}>PayMapLabs</p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
